package ai.jessie.web.profile

import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.linkButton
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text

// Step 3, A3 and A4. The verified email is never an input; it changes only at the provider.
@Composable
fun emailChangeSection(
    verifiedEmail: String?,
    state: EmailChangeUiState,
    actions: EmailChangeActions,
) {
    Section(attrs = CardStyle.toAttrs { attr("aria-labelledby", "email-change-title") }) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(attrs = { id("email-change-title") }) { Text("Bestätigte Anmelde-E-Mail") }
            P {
                Text(
                    "Du änderst die Adresse beim Anmeldedienst und bestätigst sie dort. Jessie " +
                        "übernimmt sie erst nach erneuter Anmeldung und Prüfung. Danach werden alle " +
                        "Sitzungen beendet."
                )
            }
        }
        Div(attrs = CardBodyStyle.toAttrs()) {
            verifiedEmail?.let { Div { Text("Aktuell (nur lesen): $it") } }
            emailChangeStatus(state)
            Ol(attrs = SubListStyle.toAttrs()) {
                Li {
                    Div(attrs = RowStyle.toAttrs()) {
                        themedButton(
                            "E-Mail-Änderung starten",
                            actions.start,
                            disabled = state.busy,
                        )
                        state.selfServiceUri?.let { uri ->
                            A(
                                href = uri,
                                attrs = {
                                    attr("target", "_blank")
                                    attr("rel", "noopener noreferrer")
                                },
                            ) {
                                Text("Anmeldedienst in neuem Tab öffnen")
                            }
                        }
                    }
                }
                Li {
                    Div(attrs = RowStyle.toAttrs()) {
                        Text("Kehre nach der Bestätigung hierher zurück.")
                        themedButton("Änderung prüfen", actions.check, disabled = state.busy)
                    }
                }
            }
        }
        Div(attrs = CardFootStyle.toAttrs()) {
            themedButton(
                "E-Mail-Änderung abbrechen",
                actions.cancel,
                ButtonVariant.GHOST,
                disabled = state.busy,
            )
        }
    }
}

@Composable
private fun emailChangeStatus(state: EmailChangeUiState) {
    state.error?.let { problem ->
        Div(attrs = AlertStyle.toAttrs()) {
            P(attrs = { attr("role", "alert") }) { Text(problem.message) }
            problem.destination?.let { P { A(href = it) { Text("Anmelden") } } }
        }
    }
    if (state.busy)
        P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text("Wird geprüft …") }
    state.notice?.let { P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(it) } }
}

data class EmailChangeResult(val message: String, val signInRequired: Boolean = false)

/**
 * Callback outcomes. Only `changed` reports success; Jessie cannot undo a change already made at
 * the provider, so every other outcome states that Jessie kept the previous address.
 */
fun emailChangeResult(outcome: String?) =
    when (outcome) {
        "changed" ->
            EmailChangeResult(
                "Deine bestätigte Anmelde-E-Mail wurde übernommen. Alle Jessie-Sitzungen wurden " +
                    "beendet. Bitte melde dich erneut an.",
                signInRequired = true,
            )
        "unchanged" ->
            EmailChangeResult(
                "Der Anmeldedienst meldet keine geänderte bestätigte Adresse. Jessie verwendet " +
                    "weiterhin deine bisherige Adresse."
            )
        "cancelled" ->
            EmailChangeResult(
                "Die Prüfung wurde abgebrochen. Jessie verwendet weiterhin deine bisherige Adresse."
            )
        "rejected" ->
            EmailChangeResult(
                "Die Änderung konnte nicht bestätigt werden und wurde nicht übernommen. Jessie " +
                    "verwendet weiterhin deine bisherige Adresse, auch wenn du sie beim Anmeldedienst " +
                    "bereits geändert hast. Starte die Änderung bei Bedarf erneut."
            )
        "unavailable" ->
            EmailChangeResult(
                "Die Prüfung ist vorübergehend nicht möglich. Es wurde nichts übernommen. Versuche " +
                    "es später erneut."
            )
        else -> EmailChangeResult("Kein Ergebnis einer E-Mail-Änderung vorhanden.")
    }

@Composable
fun emailChangeOutcome(result: EmailChangeResult) {
    P(attrs = { attr("role", "status") }) { Text(result.message) }
    if (result.signInRequired)
        P { linkButton("Anmelden", "/api/v1/authentication/start", ButtonVariant.PRIMARY) }
    else P { linkButton("Zurück zum Profil", "/profile") }
}
